/**
 * V7-B „Experten-Radar": einklappbare Liste von Werten, über die deine
 * YouTube-Experten reden, die aber (noch) NICHT in deiner Watchlist sind.
 * Standard eingeklappt — Entdeckungs-Hilfe, kein tägliches Pflicht-Element.
 * Zeigt nichts, wenn es nichts zu zeigen gibt.
 *
 * Audit 15.06.: pro Wert aufklappbar (Einzel-Stimmen je Experte aus der
 * Transkript-Extraktion) + 1-Klick „+ hinzufügen" (öffnet den Hinzufügen-
 * Dialog mit vorausgefülltem Symbol).
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Radar, Plus } from 'lucide-react';

import { fetchExpertRadar, fetchExpertFreshness } from '@/lib/expert-radar-read';
import { consensusDot, type ExpertStance } from '@/lib/expert-consensus';
import { StatusDot } from '@/components/ui/StatusDot';
import { cn } from '@/lib/utils';

function vorTagen(iso: string): string {
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (d <= 0) return 'heute';
  if (d === 1) return 'gestern';
  return `vor ${d} Tagen`;
}

function vorStunden(iso: string): string {
  const h = Math.floor((Date.now() - new Date(iso).getTime()) / 3_600_000);
  if (h <= 0) return 'gerade eben';
  if (h < 24) return `vor ${h} Std`;
  return vorTagen(iso);
}

const STANCE_META: Record<ExpertStance, { label: string; cls: string }> = {
  bullish: { label: '▲ bullisch', cls: 'text-emerald-400' },
  bearish: { label: '▼ bärisch', cls: 'text-red-400' },
  neutral: { label: '– neutral', cls: 'text-zinc-400' },
};

interface ExpertRadarCardProps {
  /** Öffnet den Hinzufügen-Dialog mit vorausgefülltem Symbol. */
  onAdd?: (symbol: string) => void;
}

export function ExpertRadarCard({ onAdd }: ExpertRadarCardProps) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const { data } = useQuery({
    queryKey: ['expert-radar'],
    queryFn: () => fetchExpertRadar(),
    staleTime: 5 * 60_000,
  });
  // #43-Nachfrage: sichtbar machen, DASS die Aufnahme frisch ist (getrennt vom
  // real stabilen Konsens je Wert).
  const { data: fresh } = useQuery({
    queryKey: ['expert-freshness'],
    queryFn: () => fetchExpertFreshness(),
    staleTime: 5 * 60_000,
  });
  if (!data || data.length === 0) return null;

  const freshLine = fresh?.lastCheckedAt
    ? `zuletzt geprüft ${vorStunden(fresh.lastCheckedAt)}${
        fresh.neueLetzte24h > 0 ? ` · ${fresh.neueLetzte24h} neue Stimme${fresh.neueLetzte24h === 1 ? '' : 'n'} (24 h)` : ' · keine neuen Videos (24 h)'
      }`
    : null;

  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/40">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 p-3 text-left focus-ring"
      >
        <Radar className="size-4 text-zinc-400" />
        <span className="min-w-0">
          <span className="block text-sm font-medium text-zinc-200">
            Auf dem Radar der Experten{' '}
            <span className="text-[11px] font-normal text-zinc-500">
              {data.length} {data.length === 1 ? 'Wert' : 'Werte'} — noch nicht bei dir
            </span>
          </span>
          {freshLine && (
            <span
              className={cn(
                'block text-[10px]',
                fresh && fresh.neueLetzte24h > 0 ? 'text-emerald-400/80' : 'text-zinc-500'
              )}
            >
              {freshLine}
            </span>
          )}
        </span>
        <span className="ml-auto">
          {open ? (
            <ChevronDown className="size-4 text-zinc-500" />
          ) : (
            <ChevronRight className="size-4 text-zinc-500" />
          )}
        </span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-1.5">
          <p className="text-[11px] text-zinc-500 leading-snug">
            Werte, über die deine Experten gerade reden, die du (noch) nicht beobachtest.
            Antizyklisch: eher zum Vormerken als zum Hinterherlaufen — aufnehmen entscheidest du
            selbst. Tipp auf einen Wert für die einzelnen Experten-Stimmen.
          </p>
          {data.map((e) => {
            const isExp = expanded === e.symbol;
            return (
              <div key={e.symbol} className="border-t border-zinc-800/70 pt-1.5">
                <div className="flex items-start gap-2 text-[12px]">
                  <StatusDot tone={consensusDot(e.consensus.tone)} size="sm" className="mt-1" />
                  <button
                    type="button"
                    onClick={() => setExpanded(isExp ? null : e.symbol)}
                    className="flex items-start gap-1.5 flex-1 min-w-0 text-left focus-ring"
                    title="Einzel-Stimmen aufklappen"
                  >
                    <span className="font-mono font-semibold text-zinc-200 shrink-0">{e.symbol}</span>
                    <span className="text-zinc-400 flex-1 min-w-0 leading-snug">
                      {e.consensus.satz}
                    </span>
                    {isExp ? (
                      <ChevronDown className="size-3 text-zinc-500 shrink-0 mt-0.5" />
                    ) : (
                      <ChevronRight className="size-3 text-zinc-500 shrink-0 mt-0.5" />
                    )}
                  </button>
                  {onAdd && (
                    <button
                      type="button"
                      onClick={() => onAdd(e.symbol)}
                      className="shrink-0 inline-flex items-center gap-0.5 rounded border border-zinc-700 px-1.5 py-0.5 text-[10px] text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100 focus-ring"
                      title={`${e.symbol} zur Watchlist hinzufügen`}
                    >
                      <Plus className="size-3" /> hinzufügen
                    </button>
                  )}
                </div>
                {isExp && (
                  <div className="mt-1.5 ml-4 space-y-2">
                    {e.consensus.levelHinweis && (
                      <div className="text-[11px] text-amber-200/80 leading-snug">
                        {e.consensus.levelHinweis}
                      </div>
                    )}
                    {e.consensus.stimmen.map((s, i) => {
                      const sm = STANCE_META[s.stance];
                      return (
                        <div
                          key={`${s.channelName}-${i}`}
                          className="text-[11px] border-l-2 border-zinc-800 pl-2 leading-snug"
                        >
                          <div className="flex items-baseline gap-1.5 flex-wrap">
                            <span className={`font-medium ${sm.cls}`}>{sm.label}</span>
                            <span className="text-zinc-300">{s.channelName}</span>
                            <span className="text-zinc-600">· {vorTagen(s.videoPublishedAt)}</span>
                          </div>
                          {s.summary && <div className="text-zinc-400 mt-0.5">{s.summary}</div>}
                          {s.levelNote && (
                            <div className="text-zinc-500 mt-0.5">📍 {s.levelNote}</div>
                          )}
                          {s.videoUrl && (
                            <a
                              href={s.videoUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-cockpit-accent hover:underline text-[10px]"
                            >
                              Video ansehen ↗
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
